import React, { useState, useCallback, useRef, useEffect } from 'react';
import ReactFlow, {
  MiniMap,
  Controls,
  Background,
  ReactFlowProvider,
  addEdge,
  Panel,
  useNodesState,
  useEdgesState,
  ConnectionLineType,
  useReactFlow,
  MarkerType,
  Handle,
  Position,
  EdgeLabelRenderer,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { ButtonBasic } from '@lk-gtcom/ecomlab-components';
import './ReactFlow.scss'
import { TitlePage } from '../TitlePage/TitlePage';
import { TextPage } from '../TextPage/TextPage';

// Кастомные узлы с точками соединения (без черного выделения)
const CustomNode = ({ id, data, selected }) => (
  <div style={{
    padding: '12px 16px',
    borderRadius: '8px',
    background: data.type === 'start' ? '#4CAF50' :
      data.type === 'process' ? '#2196F3' :
        data.type === 'decision' ? '#FF9800' :
          data.type === 'end' ? '#F44336' : '#9C27B0',
    color: 'white',
    minWidth: '120px',
    textAlign: 'center',
    fontWeight: 'bold',
    fontSize: '14px',
    boxShadow: selected ? '0 0 0 3px rgba(33, 150, 243, 0.5)' : '0 2px 8px rgba(0,0,0,0.2)',
    border: '2px solid transparent',
    cursor: 'move',
    transition: 'all 0.2s',
    position: 'relative',
  }}>
    {/* Точка соединения слева (вход) */}
    <Handle
      type="target"
      position={Position.Left}
      style={{
        background: '#555',
        width: '10px',
        height: '10px',
      }}
      onConnect={(params) => console.log('handle onConnect', params)}
    />

    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      {data.icon && <span>{data.icon}</span>}
      <div>
        <div>{data.label}</div>
        {data.description && (
          <div style={{ fontSize: '11px', opacity: 0.9, marginTop: '4px' }}>
            {data.description}
          </div>
        )}
      </div>
    </div>

    <div style={{
      fontSize: '10px',
      marginTop: '8px',
      paddingTop: '4px',
      borderTop: '1px solid rgba(255,255,255,0.3)',
    }}>
      {data.type}
    </div>

    {/* Точка соединения справа (выход) */}
    <Handle
      type="source"
      position={Position.Right}
      style={{
        background: '#555',
        width: '10px',
        height: '10px',
      }}
      onConnect={(params) => console.log('handle onConnect', params)}
    />

    {/* Точки сверху и снизу для решений */}
    {(data.type === 'decision' || data.type === 'input' || data.type === 'output') && (
      <>
        <Handle
          type="source"
          position={Position.Top}
          style={{
            background: '#555',
            width: '10px',
            height: '10px',
          }}
          id="top"
        />
        <Handle
          type="target"
          position={Position.Bottom}
          style={{
            background: '#555',
            width: '10px',
            height: '10px',
          }}
          id="bottom"
        />
      </>
    )}
  </div>
);

// Компонент для редактирования связи
const EdgeEditPopup = ({ edge, onUpdate, onDelete, position }) => {
  const [label, setLabel] = useState(edge?.label || '');
  const [hasStartArrow, setHasStartArrow] = useState(!!edge?.markerStart);
  const [hasEndArrow, setHasEndArrow] = useState(!!edge?.markerEnd);

  const handleSave = () => {
    const updatedEdge = {
      ...edge,
      label: label || undefined,
      markerStart: hasStartArrow ? {
        type: MarkerType.ArrowClosed,
        color: '#666',
        width: 20,
        height: 20,
      } : undefined,
      markerEnd: hasEndArrow ? {
        type: MarkerType.ArrowClosed,
        color: '#666',
        width: 20,
        height: 20,
      } : undefined,
    };
    onUpdate(updatedEdge);
  };

  const handleDelete = () => {
    onDelete(edge.id);
  };

  return (
    <div style={{
      position: 'absolute',
      left: position.x,
      top: position.y,
      background: 'white',
      padding: '15px',
      borderRadius: '8px',
      boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
      zIndex: 1000,
      minWidth: '250px',
    }}>
      <TitlePage
        small
        title="Редактирование связи"
        style={{ marginBottom: '15px' }}
      />
      
      <div style={{ marginBottom: '15px' }}>
        <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px' }}>
          Надпись на связи:
        </label>
        <input
          type="text"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          style={{
            width: '100%',
            padding: '8px',
            borderRadius: '4px',
            border: '1px solid #ddd',
            fontSize: '14px',
          }}
          placeholder="Введите текст..."
        />
      </div>

      <div style={{ marginBottom: '15px' }}>
        <label style={{ display: 'block', marginBottom: '10px', fontSize: '14px' }}>
          Стрелки:
        </label>
        <div style={{ display: 'flex', gap: '15px' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <input
              type="checkbox"
              checked={hasStartArrow}
              onChange={(e) => setHasStartArrow(e.target.checked)}
            />
            <span>В начале</span>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <input
              type="checkbox"
              checked={hasEndArrow}
              onChange={(e) => setHasEndArrow(e.target.checked)}
            />
            <span>В конце</span>
          </label>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '10px' }}>
        <ButtonBasic
          green
          onClick={handleSave}
          text="Сохранить"
          width="auto"
          size="34px"
        />
        <ButtonBasic
          red
          onClick={handleDelete}
          text="Удалить"
          width="auto"
          size="34px"
        />
      </div>
    </div>
  );
};

// Панель инструментов с элементами для добавления
const Toolbar = ({ 
  onAddNode, 
  onSave, 
  onClear, 
  onUndo, 
  canUndo, 
  onEditNode, 
  hasSelectedNode,
  onEditEdge,
  hasSelectedEdge 
}) => {
  const nodeTypes = [
    { type: 'start', label: 'Начало', icon: '▶️' },
    { type: 'process', label: 'Процесс', icon: '⚙️' },
    { type: 'decision', label: 'Решение', icon: '❓' },
    { type: 'end', label: 'Конец', icon: '⏹️' },
    { type: 'input', label: 'Ввод', icon: '📥' },
    { type: 'output', label: 'Вывод', icon: '📤' },
  ];

  return (
    <>
      <TitlePage
        medium
        title='📐 Инструменты'
      />

      <TitlePage
        medium
        title='Добавить узел:'
        style={{ fontSize: '16px' }}
      />

      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
        {nodeTypes.map((node) => (
          <ButtonBasic
            whiteGreen
            key={node.type}
            onClick={() => onAddNode(node.type)}
            text={node.icon + ' ' + node.label}
            width='auto'
            size='34px'
          />
        ))}
      </div>

      <div className='scheme-flow-edit__info'>
        <TitlePage
          medium
          title='Как создавать связи:'
          style={{ fontSize: '16px', color: '#FB923C' }}
        />
        <ul className='scheme-flow-edit__info-list'>
          <li>Наведите на узел - появятся <strong>синие точки</strong></li>
          <li>Кликните на точку <strong>справа</strong> (выход)</li>
          <li>Перетащите к точке <strong>слева</strong> другого узла</li>
        </ul>
      </div>

      <ButtonBasic
        orange
        onClick={onEditNode}
        disabled={!hasSelectedNode}
        text={hasSelectedNode ? '✏️ Редактировать выбранный узел' : '✏️ Выберите узел для редактирования'}
        width='100%'
        size='34px'
      />

      <ButtonBasic
        green
        onClick={onSave}
        text={'💾 Сохранить схему'}
        width='100%'
        size='34px'
      />

      <ButtonBasic
        grey
        onClick={onUndo}
        disabled={!canUndo}
        text={'↩️ Отменить'}
        width='100%'
        size='34px'
      />

      <ButtonBasic
        red
        onClick={onClear}
        text={'🗑️ Очистить'}
        width='100%'
        size='34px'
      />
    </>
  );
};

// Основной компонент редактора
const FlowEditor = () => {
  const reactFlowWrapper = useRef(null);
  const { setViewport, screenToFlowPosition, getNodes } = useReactFlow();

  // Начальные узлы
  const [nodes, setNodes, onNodesChange] = useNodesState([
    {
      id: '1',
      type: 'custom',
      position: { x: 100, y: 100 },
      data: {
        label: 'Начало процесса',
        type: 'start',
        icon: '▶️',
        description: 'Запуск workflow'
      },
    },
    {
      id: '2',
      type: 'custom',
      position: { x: 300, y: 100 },
      data: {
        label: 'Анализ данных',
        type: 'process',
        icon: '📊',
        description: 'Обработка информации'
      },
    },
    {
      id: '3',
      type: 'custom',
      position: { x: 500, y: 100 },
      data: {
        label: 'Проверка условий',
        type: 'decision',
        icon: '⚖️',
        description: 'Принятие решения'
      },
    },
  ]);

  // Начальные связи с явными стрелками
  const [edges, setEdges, onEdgesChange] = useEdgesState([
    {
      id: 'e1-2',
      source: '1',
      sourceHandle: 'right',
      target: '2',
      targetHandle: 'left',
      label: 'перейти к',
      type: 'smoothstep',
      animated: false,
      style: { stroke: '#666', strokeWidth: 2 },
      markerEnd: {
        type: MarkerType.ArrowClosed,
        color: '#666',
        width: 20,
        height: 20,
      },
    },
    {
      id: 'e2-3',
      source: '2',
      sourceHandle: 'right',
      target: '3',
      targetHandle: 'left',
      label: 'проанализировать',
      type: 'smoothstep',
      animated: false,
      style: { stroke: '#666', strokeWidth: 2 },
      markerEnd: {
        type: MarkerType.ArrowClosed,
        color: '#666',
        width: 20,
        height: 20,
      },
    },
  ]);

  // История для отмены
  const [history, setHistory] = useState([{ nodes: [...nodes], edges: [...edges] }]);
  const [historyIndex, setHistoryIndex] = useState(0);

  // Выбранные элементы
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [selectedEdgeId, setSelectedEdgeId] = useState(null);
  const [edgeEditPopup, setEdgeEditPopup] = useState(null);

  // Типы узлов
  const nodeTypes = useCallback(() => ({
    custom: CustomNode
  }), []);

  // Находим выбранные элементы
  const selectedNode = nodes.find(node => node.id === selectedNodeId);
  const selectedEdge = edges.find(edge => edge.id === selectedEdgeId);

  // Создание соединений с стрелками
  const onConnect = useCallback(
    (params) => {
      const newEdge = {
        ...params,
        id: `edge-${params.source}-${params.target}-${Date.now()}`,
        type: 'smoothstep',
        animated: false,
        style: { stroke: '#666', strokeWidth: 1 },
        label: 'новая связь',
        markerEnd: {
          type: MarkerType.ArrowClosed,
          color: '#666',
          width: 20,
          height: 20,
        },
      };

      const newEdges = addEdge(newEdge, edges);

      saveHistory({ nodes, edges: newEdges });
      setEdges(newEdges);
    },
    [edges, nodes]
  );

  // Сохранение в историю
  const saveHistory = (state) => {
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push({ nodes: [...state.nodes], edges: [...state.edges] });
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  };

  // Отмена последнего действия
  const handleUndo = () => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      const state = history[newIndex];
      setNodes([...state.nodes]);
      setEdges([...state.edges]);
      setHistoryIndex(newIndex);
      setSelectedNodeId(null);
      setSelectedEdgeId(null);
      setEdgeEditPopup(null);
    }
  };

  // Добавление нового узла
  const handleAddNode = (nodeType) => {
    const newNodeId = `node-${Date.now()}`;

    let position;
    if (reactFlowWrapper.current) {
      const rect = reactFlowWrapper.current.getBoundingClientRect();
      position = screenToFlowPosition({
        x: rect.width / 2 - 60,
        y: rect.height / 2 - 30,
      });
    } else {
      position = {
        x: Math.random() * 400 + 100,
        y: Math.random() * 300 + 100
      };
    }

    const newNode = {
      id: newNodeId,
      type: 'custom',
      position,
      data: getNodeData(nodeType, newNodeId),
    };

    const newNodes = [...nodes, newNode];
    saveHistory({ nodes: newNodes, edges });
    setNodes(newNodes);
  };

  // Генерация данных для узла по типу
  const getNodeData = (type, id) => {
    const types = {
      start: {
        label: `Начало`,
        type: 'start',
        icon: '▶️',
        description: 'Стартовая точка',
      },
      process: {
        label: `Процесс`,
        type: 'process',
        icon: '⚙️',
        description: 'Операция',
      },
      decision: {
        label: `Решение`,
        type: 'decision',
        icon: '❓',
        description: 'Условие',
      },
      end: {
        label: `Конец`,
        type: 'end',
        icon: '⏹️',
        description: 'Финиш',
      },
      input: {
        label: `Ввод`,
        type: 'input',
        icon: '📥',
        description: 'Данные',
      },
      output: {
        label: `Вывод`,
        type: 'output',
        icon: '📤',
        description: 'Результат',
      },
    };

    return types[type] || types.process;
  };

  // Обработка клика по узлу
  const onNodeClick = useCallback((event, node) => {
    setSelectedNodeId(node.id);
    setSelectedEdgeId(null);
    setEdgeEditPopup(null);
  }, []);

  // Обработка клика по связи
  const onEdgeClick = useCallback((event, edge) => {
    setSelectedEdgeId(edge.id);
    setSelectedNodeId(null);
    
    // Показываем попап редактирования рядом с кликом
    const bounds = event.currentTarget.getBoundingClientRect();
    setEdgeEditPopup({
      edge,
      position: { x: event.clientX - bounds.left, y: event.clientY - bounds.top },
    });
  }, []);

  // Обработка двойного клика по узлу
  const onNodeDoubleClick = useCallback((event, node) => {
    editNodeLabel(node.id, node.data.label);
  }, [nodes, edges]);

  // Функция для редактирования узла
  const editNodeLabel = (nodeId, currentLabel) => {
    const newLabel = prompt('Введите новое название узла:', currentLabel);
    if (newLabel !== null && newLabel.trim() !== '') {
      const newNodes = nodes.map((n) => {
        if (n.id === nodeId) {
          return {
            ...n,
            data: {
              ...n.data,
              label: newLabel.trim(),
            },
          };
        }
        return n;
      });

      saveHistory({ nodes: newNodes, edges });
      setNodes(newNodes);
    }
  };

  // Функция для обновления связи
  const updateEdge = useCallback((updatedEdge) => {
    const newEdges = edges.map(edge => 
      edge.id === updatedEdge.id ? updatedEdge : edge
    );
    
    saveHistory({ nodes, edges: newEdges });
    setEdges(newEdges);
    setEdgeEditPopup(null);
    setSelectedEdgeId(null);
  }, [edges, nodes]);

  // Функция для удаления связи
  const deleteEdge = useCallback((edgeId) => {
    const newEdges = edges.filter(edge => edge.id !== edgeId);
    
    saveHistory({ nodes, edges: newEdges });
    setEdges(newEdges);
    setEdgeEditPopup(null);
    setSelectedEdgeId(null);
  }, [edges, nodes]);

  // Редактирование выбранного узла
  const handleEditNode = useCallback(() => {
    if (!selectedNodeId) {
      alert('Выберите узел для редактирования (кликните по нему)');
      return;
    }

    const node = nodes.find(n => n.id === selectedNodeId);
    if (!node) {
      alert('Узел не найден');
      return;
    }

    editNodeLabel(selectedNodeId, node.data.label);
  }, [selectedNodeId, nodes, edges]);

  // Редактирование выбранной связи
  const handleEditEdge = useCallback(() => {
    if (!selectedEdgeId) {
      alert('Выберите связь для редактирования (кликните по ней)');
      return;
    }

    const edge = edges.find(e => e.id === selectedEdgeId);
    if (!edge) {
      alert('Связь не найдена');
      return;
    }

    // Показываем попап в центре экрана
    setEdgeEditPopup({
      edge,
      position: { x: window.innerWidth / 2 - 125, y: window.innerHeight / 2 - 100 },
    });
  }, [selectedEdgeId, edges]);

  // Сохранение схемы
  const handleSave = () => {
    const flowData = {
      nodes,
      edges,
      savedAt: new Date().toISOString(),
    };

    localStorage.setItem('savedFlow', JSON.stringify(flowData, null, 2));

    const dataStr = JSON.stringify(flowData, null, 2);
    const dataUri = `data:application/json;charset=utf-8,${encodeURIComponent(dataStr)}`;

    const exportFileDefaultName = `схема_${new Date().toLocaleDateString()}.json`;
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();

    alert('✅ Схема сохранена!');
  };

  // Очистка схемы
  const handleClear = () => {
    if (window.confirm('Очистить всю схему?')) {
      setNodes([]);
      setEdges([]);
      setSelectedNodeId(null);
      setSelectedEdgeId(null);
      setEdgeEditPopup(null);
      saveHistory({ nodes: [], edges: [] });
    }
  };

  // Загрузка сохраненной схемы
  const loadSavedFlow = () => {
    const saved = localStorage.getItem('savedFlow');
    if (saved) {
      try {
        const flowData = JSON.parse(saved);

        const edgesWithMarkers = flowData.edges.map(edge => ({
          ...edge,
          markerEnd: edge.markerEnd || {
            type: MarkerType.ArrowClosed,
            color: '#666',
            width: 20,
            height: 20,
          },
        }));

        setNodes(flowData.nodes);
        setEdges(edgesWithMarkers);
        saveHistory({ nodes: flowData.nodes, edges: edgesWithMarkers });
        alert('Схема загружена из сохранения');
      } catch (error) {
        console.error('Ошибка загрузки:', error);
        alert('Ошибка при загрузке схемы');
      }
    } else {
      alert('Нет сохраненных схем');
    }
  };

  // Сброс выбора при клике на холст
  const onPaneClick = useCallback(() => {
    setSelectedNodeId(null);
    setSelectedEdgeId(null);
    setEdgeEditPopup(null);
  }, []);

  // Обработка изменений узлов
  const handleNodesChange = useCallback(
    (changes) => {
      onNodesChange(changes);
      
      changes.forEach(change => {
        if (change.type === 'select') {
          if (change.selected) {
            setSelectedNodeId(change.id);
            setSelectedEdgeId(null);
            setEdgeEditPopup(null);
          } else if (selectedNodeId === change.id) {
            setSelectedNodeId(null);
          }
        }
      });
    },
    [onNodesChange, selectedNodeId]
  );

  // Обработка изменений связей
  const handleEdgesChange = useCallback(
    (changes) => {
      onEdgesChange(changes);
      
      changes.forEach(change => {
        if (change.type === 'select') {
          if (change.selected) {
            setSelectedEdgeId(change.id);
            setSelectedNodeId(null);
            setEdgeEditPopup(null);
          } else if (selectedEdgeId === change.id) {
            setSelectedEdgeId(null);
          }
        }
      });
    },
    [onEdgesChange, selectedEdgeId]
  );

  // Обновление узлов при изменении
  useEffect(() => {
    if (selectedNodeId && !nodes.find(n => n.id === selectedNodeId)) {
      setSelectedNodeId(null);
    }
  }, [nodes, selectedNodeId]);

  // Обновление связей при изменении
  useEffect(() => {
    if (selectedEdgeId && !edges.find(e => e.id === selectedEdgeId)) {
      setSelectedEdgeId(null);
      setEdgeEditPopup(null);
    }
  }, [edges, selectedEdgeId]);

  // Настройки по умолчанию для всех новых связей
  const defaultEdgeOptions = {
    type: 'smoothstep',
    animated: true,
    style: { stroke: '#666', strokeWidth: 2 },
    markerEnd: {
      type: MarkerType.ArrowClosed,
      color: '#666',
      width: 20,
      height: 20,
    },
  };

  return (
    <div className='scheme-flow'>
      {/* Боковая панель с инструментами */}
      <div className='scheme-flow-edit' style={{
        width: '300px',
      }}>
        <Toolbar
          onAddNode={handleAddNode}
          onSave={handleSave}
          onClear={handleClear}
          onUndo={handleUndo}
          canUndo={historyIndex > 0}
          onEditNode={handleEditNode}
          hasSelectedNode={!!selectedNodeId}
          onEditEdge={handleEditEdge}
          hasSelectedEdge={!!selectedEdgeId}
        />

        <div className='scheme-flow-edit__info'
          style={{ background: '#e1f2ff' }}
        >
          <TitlePage
            medium
            title='Инструкция: '
            style={{ fontSize: '16px', color: '#1890FF' }}
          />
          <ul className='scheme-flow-edit__info-list'>
            <li>Перетаскивайте узлы мышкой</li>
            <li><strong>Наведите на узел</strong> - появятся синие точки</li>
            <li><strong>Точка справа</strong> - выход (отсюда тянем)</li>
            <li><strong>Точка слева</strong> - вход (сюда тянем)</li>
            <li><strong>Клик по связи</strong> - редактирование стрелки</li>
            <li>Двойной клик по узлу для редактирования</li>
            <li>Или выберите элемент и нажмите "Редактировать"</li>
            <li>Используйте колесо мыши для масштабирования</li>
            <li>Перетаскивайте холст зажатой правой кнопкой</li>
          </ul>

          <ButtonBasic
            blue
            onClick={loadSavedFlow}
            text={'Загрузить схему'}
            width='100%'
            size='34px'
          />
        </div>

        <div style={{
          fontSize: '12px',
          color: '#888',
          textAlign: 'center',
        }}>
          <p>Узлов: {nodes.length}</p>
          <p>Связей: {edges.length}</p>
          <p>История: {historyIndex + 1} / {history.length}</p>
          {selectedNodeId && selectedNode && (
            <p style={{ color: '#2196F3', fontWeight: 'bold' }}>
              Выбран узел: {selectedNode.data.label}
            </p>
          )}
          {selectedEdgeId && selectedEdge && (
            <p style={{ color: '#4CAF50', fontWeight: 'bold' }}>
              Выбрана связь: {selectedEdge.label || 'без названия'}
            </p>
          )}
        </div>
      </div>

      {/* Область рисования */}
      <div className='scheme-flow-board' style={{ flex: 1, position: 'relative' }}>
        <div ref={reactFlowWrapper} style={{ width: '100%', height: '100%' }}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={handleNodesChange}
            onEdgesChange={handleEdgesChange}
            onConnect={onConnect}
            onNodeClick={onNodeClick}
            onEdgeClick={onEdgeClick}
            onNodeDoubleClick={onNodeDoubleClick}
            onPaneClick={onPaneClick}
            nodeTypes={nodeTypes()}
            connectionLineType={ConnectionLineType.SmoothStep}
            connectionLineStyle={{ stroke: '#666', strokeWidth: 2 }}
            fitView
            fitViewOptions={{ padding: 0.2 }}
            defaultEdgeOptions={defaultEdgeOptions}
            edgesFocusable={true}
            edgesUpdatable={true}
            connectionMode="loose"
            nodesDraggable={true}
            nodesConnectable={true}
            elementsSelectable={true}
            selectNodesOnDrag={true}
            multiSelectionKeyCode={null}
            selectionKeyCode={null}
          >
            {/* Попап редактирования связи */}
            {edgeEditPopup && (
              <EdgeEditPopup
                edge={edgeEditPopup.edge}
                position={edgeEditPopup.position}
                onUpdate={updateEdge}
                onDelete={deleteEdge}
              />
            )}

            <Panel position="top-right" style={{ background: 'white', padding: '10px', borderRadius: '8px' }}>
              <div style={{ fontSize: '12px', color: '#666' }}>
                {nodes.length} узлов, {edges.length} связей
                {selectedNodeId && (
                  <div style={{ color: '#2196F3', marginTop: '5px' }}>
                    Выбран узел: {nodes.find(n => n.id === selectedNodeId)?.data.label}
                  </div>
                )}
                {selectedEdgeId && (
                  <div style={{ color: '#4CAF50', marginTop: '5px' }}>
                    Выбрана связь: {edges.find(e => e.id === selectedEdgeId)?.label || 'без названия'}
                  </div>
                )}
              </div>
            </Panel>

            <Controls
              style={{
                boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                borderRadius: '8px',
              }}
            />
            <MiniMap
              style={{
                backgroundColor: 'white',
                border: '1px solid #e0e0e0',
                borderRadius: '8px',
              }}
              zoomable
              pannable
              nodeColor={(node) => {
                if (node.data.type === 'start') return '#4CAF50';
                if (node.data.type === 'process') return '#2196F3';
                if (node.data.type === 'decision') return '#FF9800';
                if (node.data.type === 'end') return '#F44336';
                return '#9C27B0';
              }}
            />
            <Background
              variant="dots"
              gap={16}
              size={1}
              color="#e0e0e0"
            />
          </ReactFlow>
        </div>
      </div>
    </div>
  );
};

// Обертка с провайдером
const SimpleFlowEditor = () => (
  <ReactFlowProvider>
    <FlowEditor />
  </ReactFlowProvider>
);

export default SimpleFlowEditor;